import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton } from '../../components/SignOutButton';
import { readClaims, useSession } from '../../hooks/useSession';
import { fetchWithAuth } from '../../lib/accessToken';

/**
 * Error clusters — Feature 2 v1 dashboard. Section 7 channel #1
 * (Internal IE dashboard, super_admin only). Hits the IE Worker at
 * `/v1/dashboard/errors?window=1d|7d|30d`.
 *
 * Cross-tenant operational view — same Section 8 layer 2 exception as
 * Feature 1's engine-telemetry view. No PII; the chunk-2 normalizer
 * dropped everything outside the allow-list before persistence.
 */

type WindowKey = '1d' | '7d' | '30d';

const WINDOWS: readonly { key: WindowKey; label: string }[] = [
  { key: '1d', label: 'Last 1d' },
  { key: '7d', label: 'Last 7d' },
  { key: '30d', label: 'Last 30d' },
];

interface ResponseCluster {
  clusterKey: string;
  label: string;
  projectCount: number;
  totalCount: number;
  totalUserCount: number | null;
  firstSeen: string | null;
  lastSeen: string | null;
  projects: string[];
  memberSentryIds: { project: string; sentryId: string }[];
  mostRecentSnapshotDate: string;
}

interface ErrorClustersResponse {
  windowDays: number;
  builtAtMs: number;
  totalClusters: number;
  clusters: ResponseCluster[];
}

interface ApiError {
  error: string;
  detail: string;
}

type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: ErrorClustersResponse }
  | { kind: 'error'; status: number; message: string };

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function SuperErrorClusters() {
  const { session, userId } = useSession();
  const { appRole } = readClaims(session);
  const [windowKey, setWindowKey] = useState<WindowKey>('7d');
  const [state, setState] = useState<FetchState>({ kind: 'idle' });
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const base = ieBaseUrl();
    if (!base) {
      setState({
        kind: 'error',
        status: 0,
        message: 'VITE_INTELLIGENCE_BASE_URL is not configured. Set it in apps/web/.env.local.',
      });
      return;
    }
    if (userId === null) {
      setState({ kind: 'error', status: 0, message: 'No active session.' });
      return;
    }
    setState({ kind: 'loading' });
    const url = `${base}/v1/dashboard/errors?window=${windowKey}&limit=50`;
    fetchWithAuth(url)
      .then(async (res) => {
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as ApiError | null;
          setState({
            kind: 'error',
            status: res.status,
            message: body?.detail ?? `HTTP ${res.status}`,
          });
          return;
        }
        const data = (await res.json()) as ErrorClustersResponse;
        setState({ kind: 'ok', data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({ kind: 'error', status: 0, message });
      });
    return () => {
      cancelled = true;
    };
  }, [windowKey, refreshNonce, userId]);

  return (
    <main style={{ padding: '2rem', maxWidth: '76rem', margin: '0 auto' }}>
      <div
        style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.25rem',
            }}
          >
            Error clusters
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Signed in as {session?.user.email ?? 'unknown'} ·{' '}
            <Badge variant="outline">{appRole ?? 'no role'}</Badge> ·{' '}
            <Link to="/super/dashboard" style={{ color: 'var(--brand-primary)' }}>
              ← Super dashboard
            </Link>
          </p>
        </div>
        <SignOutButton redirectTo="/super" />
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWindowKey(w.key)}
            type="button"
            style={{
              padding: '0.4rem 0.9rem',
              borderRadius: '0.4rem',
              border: '1px solid var(--border)',
              background: windowKey === w.key ? 'var(--brand-primary)' : 'var(--surface-elev)',
              color: windowKey === w.key ? 'var(--brand-primary-fg)' : 'var(--text)',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            {w.label}
          </button>
        ))}
        <button
          onClick={() => setRefreshNonce((n) => n + 1)}
          type="button"
          disabled={state.kind === 'loading'}
          style={{
            padding: '0.4rem 0.9rem',
            borderRadius: '0.4rem',
            border: '1px solid var(--border)',
            background: 'var(--surface-elev)',
            color: 'var(--text)',
            fontWeight: 600,
            fontSize: '0.85rem',
            cursor: state.kind === 'loading' ? 'wait' : 'pointer',
            marginLeft: 'auto',
          }}
        >
          {state.kind === 'loading' ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {state.kind === 'idle' || state.kind === 'loading' ? (
        <Card>
          <CardContent>
            <p style={{ color: 'var(--text-muted)' }}>
              {state.kind === 'idle' ? 'Initializing…' : 'Loading clusters…'}
            </p>
          </CardContent>
        </Card>
      ) : state.kind === 'error' ? (
        <Card>
          <CardHeader>
            <CardTitle>Clusters unavailable</CardTitle>
            <CardDescription>HTTP {state.status || 'no-network'}</CardDescription>
          </CardHeader>
          <CardContent>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                color: 'var(--semantic-danger)',
                fontFamily: 'var(--font-mono, ui-monospace)',
                fontSize: '0.85rem',
                margin: 0,
              }}
            >
              {state.message}
            </pre>
          </CardContent>
        </Card>
      ) : (
        <ClustersView data={state.data} />
      )}
    </main>
  );
}

function ClustersView({ data }: { data: ErrorClustersResponse }) {
  if (data.clusters.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No errors in window</CardTitle>
          <CardDescription>
            Last {data.windowDays} day{data.windowDays === 1 ? '' : 's'} produced 0 clusters. The
            daily pull job has either not run yet for this date or every project returned an empty
            issue list.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>{data.totalClusters} clusters</CardTitle>
        <CardDescription>
          Across {data.windowDays} day{data.windowDays === 1 ? '' : 's'}. Each row shows the most
          recent snapshot per cluster. Cross-project clusters (projects &gt; 1) point at the same
          root cause showing up in multiple Sentry projects.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <thead>
            <tr>
              <Th>Cluster</Th>
              <Th align="right">Total</Th>
              <Th align="right">Users</Th>
              <Th>Projects</Th>
              <Th>Last seen</Th>
              <Th>Snapshot</Th>
              <Th>Members</Th>
            </tr>
          </thead>
          <tbody>
            {data.clusters.map((c) => (
              <ClusterRow key={c.clusterKey} cluster={c} />
            ))}
          </tbody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ClusterRow({ cluster }: { cluster: ResponseCluster }) {
  const crossProject = cluster.projectCount > 1;
  return (
    <tr>
      <Td>
        <div
          style={{
            fontWeight: crossProject ? 700 : 400,
            color: crossProject ? 'var(--semantic-warning)' : 'var(--text)',
          }}
        >
          {cluster.label}
        </div>
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            marginTop: '0.15rem',
            wordBreak: 'break-all',
          }}
        >
          {cluster.clusterKey}
        </div>
      </Td>
      <Td align="right">
        <strong>{cluster.totalCount.toLocaleString()}</strong>
      </Td>
      <Td align="right">
        {cluster.totalUserCount === null ? '—' : cluster.totalUserCount.toLocaleString()}
      </Td>
      <Td>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
          {cluster.projects.map((p) => (
            <Badge key={p} variant="outline">
              {p.replace('auctisync-', '')}
            </Badge>
          ))}
        </div>
      </Td>
      <Td>{formatTs(cluster.lastSeen)}</Td>
      <Td>{cluster.mostRecentSnapshotDate}</Td>
      <Td>
        <details>
          <summary
            style={{
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: 'var(--text-muted)',
            }}
          >
            {cluster.memberSentryIds.length} issue{cluster.memberSentryIds.length === 1 ? '' : 's'}
          </summary>
          <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, fontSize: '0.75rem' }}>
            {cluster.memberSentryIds.map((m) => (
              <li key={`${m.project}-${m.sentryId}`} style={{ color: 'var(--text-muted)' }}>
                {m.project.replace('auctisync-', '')}: {m.sentryId}
              </li>
            ))}
          </ul>
        </details>
      </Td>
    </tr>
  );
}

function Table({ children }: { children: ReactNode }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
        }}
      >
        {children}
      </table>
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: '0.5rem 0.6rem',
        borderBottom: '1px solid var(--border)',
        color: 'var(--text-muted)',
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children: ReactNode; align?: 'right' | 'left' }) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: '0.55rem 0.6rem',
        borderBottom: '1px solid var(--border-subtle, var(--border))',
        color: 'var(--text)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

function formatTs(iso: string | null): string {
  if (iso === null) return '—';
  return iso.replace('T', ' ').replace(/\..*$/, '').slice(0, 16);
}
